import { gql } from "lib/apolloClient";
import { rubricFragment } from "Rubric/modules/RubricFragments";
import { checklistFragment } from "Checklist/modules/ChecklistFragments";
import { singlePointRubricFragment } from "SinglePointRubric/modules/SinglePointRubricFragments";
import { attachmentFragment } from "modules/CommonFragments";
import {
  standardRubricElementsFragment,
  standardRubricFragment,
} from "AppComponents/StandardRubric/modules/StandardRubricFragments";
import { gradeScaleToolFragment } from "AppComponents/AssessmentGradeScaleTool/modules/AssessmentGradeScaleToolFragments";

export const aiTutorFragment = {
  criteria: gql`
    fragment analysisCriteriaItem on AnalysisCriteria {
      id
      label
      description
      displaySequence
      metadata
    }
  `,
  indicator: gql`
    fragment analysisIndicatorItem on AnalysisIndicator {
      id
      label
      displaySequence
    }
  `,
};

export const mypObjectiveRubricDescriptorFragmnet = {
  mypObjectiveRubricDescriptor: gql`
    fragment mypObjectiveRubricDescriptorItem on MypObjectiveRubricDescriptor {
      id
      label
      objective {
        id
        label
        parent
        displaySequence
      }
      achievementLevel {
        id
        label
        displaySequence
      }
    }
  `,
};

export const mypCriteriaWithDescriptorFragmnet = {
  mypCriteriaWithDescriptor: gql`
    fragment mypCriteriaWithDescriptorItem on PlannerElementNode {
      id
      objectiveRubricDescriptors {
        ...mypObjectiveRubricDescriptorItem
      }
    }
    ${mypObjectiveRubricDescriptorFragmnet.mypObjectiveRubricDescriptor}
  `,
};

export const mypObjectiveRubricCriteriaFragment = {
  mypObjectiveRubricCriteria: gql`
    fragment mypObjectiveRubricCriteriaItem on PlannerElementNode {
      id
      label
      type
      highestMark
      parent
      children
      subText
      objectiveRubricDescriptors {
        id
        achievementLevel {
          id
          label
          subText
        }
        label
      }
    }
  `,
};

export const assessmentToolFragment = {
  anecdote: gql`
    fragment anecdoteItem on Anecdote {
      id
      message
    }
  `,
  exemplar: gql`
    fragment exemplarItem on Exemplar {
      id
      message
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,
  mypObjectiveRubric: gql`
    fragment mypObjectiveRubricItem on MypObjectiveRubric {
      id
      label
      genericTags {
        id
        label
      }
      descriptors {
        ...mypObjectiveRubricDescriptorItem
      }
      criteria {
        ...mypObjectiveRubricCriteriaItem
      }
      presetRubric {
        id
        subType
        genericTags {
          id
          label
        }
      }
      isGlobal
    }
    ${mypObjectiveRubricDescriptorFragmnet.mypObjectiveRubricDescriptor}
    ${mypObjectiveRubricCriteriaFragment.mypObjectiveRubricCriteria}
  `,
  dpAssessmentRubric: gql`
    fragment dpAssessmentRubricItem on MypObjectiveRubric {
      id
      label
      genericTags {
        id
        label
      }
      criteria {
        ...mypObjectiveRubricCriteriaItem
      }
      subType
    }
    ${mypObjectiveRubricCriteriaFragment.mypObjectiveRubricCriteria}
  `,
  mypObjectiveRubricWithPresetData: gql`
    fragment mypObjectiveRubricItem on MypObjectiveRubric {
      id
      label
      genericTags {
        id
        label
      }
      descriptors {
        ...mypObjectiveRubricDescriptorItem
      }
      criteria {
        ...mypObjectiveRubricCriteriaItem
      }
      presetRubric {
        id
        criteria {
          ...mypObjectiveRubricCriteriaItem
        }
        subType
        genericTags {
          id
          label
        }
      }
    }
    ${mypObjectiveRubricDescriptorFragmnet.mypObjectiveRubricDescriptor}
    ${mypObjectiveRubricCriteriaFragment.mypObjectiveRubricCriteria}
  `,
  ibGrade: gql`
    fragment ibGradeItem on AcademicCriteriaSet {
      id
      label
      academicCriteriaValues {
        id
        label
        scoreValues {
          id
          value
          minValue
          maxValue
          valueType
          weightageType
          genericTag {
            id
            label
          }
        }
      }
      criteriaType
    }
  `,
  aiAnalysis: gql`
    fragment aiAnalysisItem on Analysis {
      id
      label
      criterias {
        ...analysisCriteriaItem
      }
      indicators {
        ...analysisIndicatorItem
      }
    }
    ${aiTutorFragment.criteria}
    ${aiTutorFragment.indicator}
  `,
  standardRubric: gql`
    fragment standardRubricItem on StandardRubric {
      id
      label
      standardRubricCriterias {
        ...standardRubricCriteria
      }
    }
    ${standardRubricElementsFragment.criteria}
  `,
  academicCriteriaSet: gql`
    fragment academicCriteriaSetItem on AcademicCriteriaSet {
      id
      label
      academicCriteriaValues {
        id
        label
        scoreValues {
          id
          value
          minValue
          maxValue
          valueType
          weightageType
          genericTag {
            id
            label
          }
        }
      }
      criteriaType
    }
  `,
  scoreAssessmentTool: gql`
    fragment scoreAssessmentToolItem on ScoreAssessmentTool {
      id
      scoreToolData {
        id
        maxScore
      }
    }
  `,
};

export const assessmentToolParentNodeFragment = {
  assessment: gql`
    fragment assessmentNodeItem on Assessment {
      id
      assessmentTools {
        edges {
          node {
            id
            assessmentToolType
            assessmentToolItem {
              id
              ... on Rubric {
                ...rubricItem
              }
              ... on Checklist {
                ...checklist
              }
              ... on Anecdote {
                ...anecdoteItem
              }
              ... on Exemplar {
                ...exemplarItem
              }
              ... on SinglePointRubric {
                ...singlePointRubricItem
              }
              ... on StandardRubric {
                ...standardRubricItem
              }
              ... on GradeScaleEntityDetail {
                ...gradeScaleEntityDetailsItem
              }
            }
            isTeacherEvaluationEnable
            isStudentEvaluationEnable
          }
        }
      }
    }
    ${assessmentToolFragment.exemplar}
    ${assessmentToolFragment.anecdote}
    ${checklistFragment.checklist}
    ${rubricFragment.rubric}
    ${singlePointRubricFragment.singlePointRubric}
    ${standardRubricFragment.standardRubric}
    ${gradeScaleToolFragment.gradeScaleEntityDetails}
  `,
  unitPlan: gql`
    fragment unitPlanNodeItem on UnitPlan {
      id
      assessmentTool {
        id
        ... on Rubric {
          ...rubricItem
        }
        ... on Checklist {
          ...checklist
        }
        ... on Anecdote {
          ...anecdoteItem
        }
        ... on Exemplar {
          ...exemplarItem
        }
        ... on SinglePointRubric {
          ...singlePointRubricItem
        }
      }
    }
    ${assessmentToolFragment.exemplar}
    ${assessmentToolFragment.anecdote}
    ${checklistFragment.checklist}
    ${rubricFragment.rubric}
    ${singlePointRubricFragment.singlePointRubric}
  `,
  question: gql`
    fragment questionItem on Question {
      id
      assessmentTools {
        edges {
          node {
            id
            assessmentToolType
            assessmentToolItem {
              id
              ... on Rubric {
                ...rubricItem
              }
              ... on Checklist {
                ...checklist
              }
              ... on Anecdote {
                ...anecdoteItem
              }
              ... on Exemplar {
                ...exemplarItem
              }
              ... on SinglePointRubric {
                ...singlePointRubricItem
              }
            }
            isTeacherEvaluationEnable
            isStudentEvaluationEnable
          }
        }
      }
    }
    ${assessmentToolFragment.exemplar}
    ${assessmentToolFragment.anecdote}
    ${checklistFragment.checklist}
    ${rubricFragment.rubric}
    ${singlePointRubricFragment.singlePointRubric}
  `,
};

export const assessmentEvaluationFragment = {
  assessmentEvaluation: gql`
    fragment assessmentEvaluationItem on AssessmentEvaluationItem {
      id
      ... on Analysis {
        ...aiAnalysisItem
      }
      ... on Rubric {
        ...rubricItem
      }
      ... on StandardRubric {
        ...standardRubricItem
      }
      ... on Checklist {
        ...checklist
      }
      ... on Anecdote {
        ...anecdoteItem
      }
      ... on Exemplar {
        ...exemplarItem
      }
      ... on SinglePointRubric {
        ...singlePointRubricItem
      }
      ... on MypObjectiveRubric {
        ...mypObjectiveRubricItem
      }
      ... on ScoreAssessmentTool {
        ...scoreAssessmentToolItem
      }
      ... on GradeScaleEntityDetail {
        ...gradeScaleEntityDetailsItem
      }
      ... on AcademicCriteriaSet {
        ...academicCriteriaSetItem
      }
    }
    ${assessmentToolFragment.exemplar}
    ${assessmentToolFragment.anecdote}
    ${checklistFragment.checklist}
    ${rubricFragment.rubric}
    ${singlePointRubricFragment.singlePointRubric}
    ${assessmentToolFragment.mypObjectiveRubric}
    ${assessmentToolFragment.aiAnalysis}
    ${standardRubricFragment.standardRubric}
    ${gradeScaleToolFragment.gradeScaleEntityDetails}
    ${assessmentToolFragment.scoreAssessmentTool}
    ${assessmentToolFragment.academicCriteriaSet}
  `,
};

export const organizationFragment = {
  scoreRubricList: gql`
    fragment organizationItem on Organization {
      id
      scoreBasedRubrics(filters: $filters) {
        ...standardRubricItem
      }
    }
    ${standardRubricFragment.standardRubric}
  `,
  standardRubricList: gql`
    fragment organizationItem on Organization {
      id
      standardBasedRubrics(filters: $filters) {
        ...standardRubricItem
      }
    }
    ${standardRubricFragment.standardRubric}
  `,
};

export const assessmentToolPlannerElementNodeFragment = {
  plannerElementNode: gql`
    fragment assessmentToolPlannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      depth
      children
      parent
      subType
      isLeaf
      metadata
      updatedAt
      tags: genericTags {
        id
        label
        icon {
          id
          url
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
          grades {
            id
            name
          }
          subjects {
            id
            name
            isArchived
          }
          subjectGroups {
            id
            name
            constants
          }
          associatedPlannerElementNodes {
            id
            label
            type
            metadata
          }
        }
      }
    }
  `,
};
